import { FaqService } from '@/services/faqManagmentService/faqApi';
import {
  Faq,
  FaqCategory,
  FaqCreateDto,
  FaqFilterParams,
  FaqInfoCount,
  FaqUpdateDto,
  PaginatedFaqResponse,
} from '@/types/faq.types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

interface faqManagementState {
  list: PaginatedFaqResponse;
  faqInfoCount: FaqInfoCount;
  item: Faq;
  loading: boolean;
  loadingInfo: boolean;
  error: string | null;
}

const initialState: faqManagementState = {
  list: {
    data: [],
    meta: {
      count: 0,
      pages: 1,
      limit: 10, // or any default
      totalPages: 1,
    },
  },
  faqInfoCount: {
    activeFaqs: 0,
    inactiveFaqs: 0,
    totalFaqs: 0,
  },
  item: {
    id: 0,
    question: '',
    answer: '',
    category: FaqCategory.GENERAL,
    status: null,
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    isDeleted: false,
  },
  loading: false,
  loadingInfo: false,
  error: null,
};

// CREATE FAQ
export const createFaq = createAsyncThunk('faq/create', async (payload: FaqCreateDto, thunkAPI) => {
  try {
    const response = await FaqService.create(payload);
    return response.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data || err.message);
  }
});

export const fetchFaqs = createAsyncThunk<PaginatedFaqResponse, FaqFilterParams>(
  'faq/fetchAll',
  async (params: Record<string, any> | undefined, thunkAPI) => {
    try {
      const response = await FaqService.getAll(params);
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const faqInfo = createAsyncThunk(
  'faq/count',
  async (params: Record<string, any> | undefined, thunkAPI) => {
    try {
      const response = await FaqService.getFaqInfo();
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

// GET one FAQ by id
export const fetchFaqById = createAsyncThunk('faq/fetchById', async (id: number, thunkAPI) => {
  try {
    const response = await FaqService.getById(id);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data || err.message);
  }
});

// UPDATE FAQ
export const updateFaq = createAsyncThunk(
  'faq/update',
  async ({ id, payload }: { id: number; payload: FaqUpdateDto }, thunkAPI) => {
    try {
      const response = await FaqService.update(id, payload);
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const updateFaqStatus = createAsyncThunk(
  'faq/update-status',
  async ({ id, payload }: { id: number; payload: Partial<FaqUpdateDto> }, thunkAPI) => {
    try {
      const response = await FaqService.update(id, payload);
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

// DELETE FAQ
export const deleteFaq = createAsyncThunk('faq/delete', async (id: number, thunkAPI) => {
  try {
    const response = await FaqService.remove(id);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data || err.message);
  }
});

// ========================
//        SLICE
// ========================

const faqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    updateFaqInList(state, action: PayloadAction<{ id: number; updates: Partial<Faq> }>) {
      const index = state.list.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.list.data[index] = {
          ...state.list.data[index],
          ...action.payload.updates,
          updatedAt: new Date().toISOString(), // Update timestamp
        };
      }
    },
    removeFaqFromList(state, action: PayloadAction<number>) {
      state.list.data = state.list.data.filter((item) => item.id !== action.payload);
      if (state.list.meta.count > 0) {
        state.list.meta.count -= 1;
      }
    },
    updateFaqStats(
      state,
      action: PayloadAction<{
        statusChange?: { oldStatus: boolean; newStatus: boolean };
        deleted?: boolean;
      }>,
    ) {
      const { statusChange, deleted } = action.payload;

      if (deleted) {
        state.faqInfoCount.totalFaqs = Math.max(0, state.faqInfoCount.totalFaqs - 1);
      }

      if (statusChange) {
        if (statusChange.oldStatus === true && statusChange.newStatus === false) {
          state.faqInfoCount.activeFaqs = Math.max(0, state.faqInfoCount.activeFaqs - 1);
          state.faqInfoCount.inactiveFaqs += 1;
        } else if (statusChange.oldStatus === false && statusChange.newStatus === true) {
          state.faqInfoCount.inactiveFaqs = Math.max(0, state.faqInfoCount.inactiveFaqs - 1);
          state.faqInfoCount.activeFaqs += 1;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Create FAQ
    builder.addCase(createFaq.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
    });

    builder
      // Fetch all FAQs
      .addCase(fetchFaqs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFaqs.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch one FAQ
    builder
      .addCase(fetchFaqById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFaqById.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.item = action.payload?.data || null;
      })
      .addCase(fetchFaqById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(faqInfo.pending, (state) => {
        state.loadingInfo = true;
        state.error = null;
      })
      .addCase(faqInfo.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingInfo = false;
        state.faqInfoCount = action.payload?.data || null;
      })
      .addCase(faqInfo.rejected, (state, action) => {
        state.loadingInfo = false;
        state.error = action.payload as string;
      });

    // Update FAQ
    builder.addCase(updateFaq.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
    });

    // Update FAQ Status (lightweight)
    builder
      .addCase(updateFaqStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateFaqStatus.fulfilled, (state) => {})
      .addCase(updateFaqStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete FAQ
    builder.addCase(deleteFaq.fulfilled, (state) => {
      state.loading = false;
    });
  },
});

export const { clearError, updateFaqInList, removeFaqFromList, updateFaqStats } = faqSlice.actions;
export default faqSlice.reducer;
